import { useEffect, useState, useCallback } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Module-level cache so the watchlist is shared across pages without
 * refetching on every navigation. Mutations (add / remove) update both
 * the cache and the local state in lock-step.
 */
let _cache = null;
const _listeners = new Set();

/**
 * useWatchlist -- exposes the current watchlist plus add/remove helpers.
 * Updates fan out to every mounted hook via a tiny pub/sub so a pin
 * toggle on the Mentions list reflects on the Dashboard widget without
 * a refetch.
 */
export const useWatchlist = () => {
  const [data, setData] = useState(_cache);
  const [loading, setLoading] = useState(_cache === null);
  const [error, setError] = useState(null);

  // Subscribe to cache updates from siblings.
  useEffect(() => {
    const update = (next) => setData(next);
    _listeners.add(update);
    return () => { _listeners.delete(update); };
  }, []);

  // Initial load if cache is cold.
  useEffect(() => {
    if (_cache !== null) return;
    let cancelled = false;
    setLoading(true);
    apiCall(API_CONFIG.endpoints.watchlist())
      .then((res) => {
        if (cancelled) return;
        publish(res);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load watchlist', err);
        setError('Failed to load watchlist');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const add = useCallback(async ({ kind, key, label }) => {
    const result = await apiCall(API_CONFIG.endpoints.watchlist(), {
      method: 'POST',
      body: JSON.stringify({ kind, key, label, addedAt: new Date().toISOString() }),
    });
    publish(result);
    return result;
  }, []);

  const remove = useCallback(async (kind, key) => {
    const result = await apiCall(API_CONFIG.endpoints.watchlistEntry(kind, key), {
      method: 'DELETE',
    });
    publish(result);
    return result;
  }, []);

  /** Returns whether (kind, key) is currently in the watchlist. Case-insensitive on both sides. */
  const isWatched = useCallback((kind, key) => {
    if (!data?.entries) return false;
    const k = (key || '').toUpperCase();
    return data.entries.some((e) =>
      (e.kind || '').toLowerCase() === (kind || '').toLowerCase() &&
      (e.key || '').toUpperCase() === k
    );
  }, [data]);

  return { watchlist: data, loading, error, add, remove, isWatched };
};

/**
 * Insights variant -- watchlist enriched with latest-action overlay + recent
 * conviction-change list. Backed by GET /api/watchlist/insights. Used by
 * the Dashboard widget; refetched manually since the changes are
 * dependent on the most recent reports.
 */
export const useWatchlistInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(API_CONFIG.endpoints.watchlistInsights());
      setData(res);
    } catch (err) {
      console.error('Failed to load watchlist insights', err);
      setError('Failed to load watchlist insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { insights: data, loading, error, reload };
};

function publish(next) {
  _cache = next;
  _listeners.forEach((fn) => fn(next));
}
